import { z } from 'zod';
import { TENANT_EVENT_DATA_SCHEMAS, TENANT_EVENT_TYPES } from './tenant-events.js';
/**
 * Versioned webhook contract for the HospaSense connector boundary.
 *
 * Transport requirements (enforced by the API before any parsing):
 *   - `content-type: application/json`
 *   - `x-hospasense-signature: v1=<hex hmac-sha256 over "<timestamp>.<nonce>.<raw body>">`
 *   - `x-hospasense-timestamp: <unix seconds>` within the tolerance window
 *   - `x-hospasense-nonce: <unique per delivery>` (replay-protected)
 *   - payload size within WEBHOOK_MAX_BYTES
 */
export const WEBHOOK_SIGNATURE_HEADER = 'x-hospasense-signature';
export const WEBHOOK_TIMESTAMP_HEADER = 'x-hospasense-timestamp';
export const WEBHOOK_NONCE_HEADER = 'x-hospasense-nonce';
/**
 * The event types this boundary accepts.
 *
 * The two `installation.*` types are the original fleet contract. The
 * `organisation.*` types are the tenant → Control customer-lifecycle catalogue,
 * defined in tenant-events.ts and spliced in here rather than copied, so there
 * is exactly one list of event names and no way for the two to disagree.
 */
export const WEBHOOK_EVENT_TYPES = [
    'installation.heartbeat',
    'installation.registered',
    ...TENANT_EVENT_TYPES,
];
/** Operational metadata only. Strict schemas reject unexpected fields. */
export const HeartbeatDataSchema = z
    .object({
    installationKey: z.string().min(4).max(100),
    appVersion: z.string().min(1).max(50),
    healthy: z.boolean(),
})
    .strict();
export const InstallationRegisteredDataSchema = z
    .object({
    installationKey: z.string().min(4).max(100),
    organisationExternalRef: z.string().min(1).max(100),
    deploymentType: z.enum(['cloud', 'on_prem']),
    appVersion: z.string().min(1).max(50),
})
    .strict();
export const WebhookEnvelopeSchema = z
    .object({
    /** Delivery-stable event id used for inbox deduplication. */
    eventId: z.uuid(),
    eventType: z.enum(WEBHOOK_EVENT_TYPES),
    schemaVersion: z.literal('v1'),
    occurredAt: z.iso.datetime(),
    data: z.record(z.string(), z.unknown()),
})
    .strict();
export const WEBHOOK_DATA_SCHEMAS = {
    'installation.heartbeat': HeartbeatDataSchema,
    'installation.registered': InstallationRegisteredDataSchema,
    // Spread rather than re-listed: a new tenant event type added in
    // tenant-events.ts arrives here automatically, and the Record<> type makes a
    // type name without a schema a compile error rather than a runtime 500.
    ...TENANT_EVENT_DATA_SCHEMAS,
};
//# sourceMappingURL=webhooks.js.map